import { LOCALE_NAMES, localeToMsLanguageId } from "@repo/shared";
import type {
  LocaleCode,
  MorningstarDocument,
  ProductDocumentSearchPass,
} from "@repo/shared/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./product-document-picker-dialog.i18n";

type ProductDocumentPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: MorningstarDocument[];
  /** Which search pass produced `documents` — drives the explanatory text. */
  pass: ProductDocumentSearchPass;
  /** The language section the picked document will be attached to. */
  sectionLanguage: LocaleCode;
  onSelect: (doc: MorningstarDocument) => void;
};

/**
 * Multi-match picker for Morningstar document search results. Shown for
 * several pass-1 hits, and always for fallback (other-language) results — a
 * different-language document is never attached without explicit approval.
 */
export function ProductDocumentPickerDialog({
  open,
  onOpenChange,
  documents,
  pass,
  sectionLanguage,
  onSelect,
}: ProductDocumentPickerDialogProps) {
  const t = useT(T);

  const sectionLanguageName = LOCALE_NAMES[sectionLanguage] || sectionLanguage;
  const description =
    pass === "language"
      ? t.multipleDocumentsFound
      : (localeToMsLanguageId(sectionLanguage) ? t.noLanguageMatch : t.unmappedLanguage).replaceAll(
          "{language}",
          sectionLanguageName,
        );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.selectDocument}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto">
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.language}</TableHead>
                <TableHead>{t.market}</TableHead>
                <TableHead>{t.date}</TableHead>
                <TableActionsHead>{t.action}</TableActionsHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={`${doc.language}-${doc.market}-${doc.documentDate}`}>
                  <TableCell>{doc.languageName || doc.language}</TableCell>
                  <TableCell>{doc.marketName || doc.market}</TableCell>
                  <TableCell>{doc.documentDate}</TableCell>
                  <TableActionsCell>
                    <Button type="button" size="sm" onClick={() => onSelect(doc)}>
                      {t.select}
                    </Button>
                  </TableActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
